import { useEffect, useRef, useState } from 'react';
import { useChartStore } from '@/store/chartStore';
import { fetchSharedChart, type SharedChart } from '@/services/shareChart';
import { applyTemplateToChart } from '@/components/Chart/applyTemplate';

function stripShareParam(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete('s');
  window.history.replaceState(window.history.state, '', url.toString());
}

/**
 * Opens a `?s=<id>` share link: fetches the snapshot, switches to its timeframe,
 * then applies its indicators/settings/drawings once the chart is ready.
 * The symbol needs no handling here — it's the URL path, which ChartPage already applies.
 */
export function useApplySharedChart(): void {
  // Captured once at load, same one-shot pattern as `?promo=` in App.tsx.
  const [shareId] = useState(() => new URLSearchParams(window.location.search).get('s'));
  const [shared, setShared] = useState<SharedChart | null>(null);
  const applied = useRef(false);

  const chart            = useChartStore((s) => s.chart);
  const chartInitialized = useChartStore((s) => s.chartInitialized);
  const loadedSymbol     = useChartStore((s) => s.loadedSymbol);
  const symbol           = useChartStore((s) => s.symbol);
  const timeframe        = useChartStore((s) => s.timeframe);
  const setTimeframe     = useChartStore((s) => s.setTimeframe);

  useEffect(() => {
    if (!shareId) return;
    let cancelled = false;
    fetchSharedChart(shareId)
      .then((result) => {
        if (cancelled) return;
        if (result) setShared(result);
        else stripShareParam(); // dead link — just show the plain chart
      })
      .catch(() => { /* transient failure: keep ?s= so a refresh retries */ });
    return () => { cancelled = true; };
  }, [shareId]);

  useEffect(() => {
    if (shared && shared.timeframe !== timeframe && !applied.current) setTimeframe(shared.timeframe);
    // Only react to the snapshot arriving — later manual timeframe changes are the viewer's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shared]);

  useEffect(() => {
    if (!shared || applied.current) return;
    if (!chart || !chartInitialized || loadedSymbol !== symbol) return;
    if (timeframe !== shared.timeframe) return;

    applied.current = true;
    // Drawings are only meaningful on the symbol they were drawn on.
    const template = shared.symbol === symbol ? shared.template : { ...shared.template, drawings: undefined };
    // Shown in full to every viewer; the creator's plan was already enforced when the link was made.
    applyTemplateToChart(chart, template, { symbol, allowAdvanced: true });
    stripShareParam();
  }, [shared, chart, chartInitialized, loadedSymbol, symbol, timeframe]);
}
